import { useEffect } from "react";
import { type FieldValues, type UseFormReturn } from "react-hook-form";
import dayjs from "dayjs";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type AdvancedDateRangeValue } from "~/common/components/AdvancedDateRangePicker/AdvancedDateRangePicker";
import { getShortcutByKey, type ShortcutKey } from "~/common/components/AdvancedDateRangePicker/shortcuts";

interface UseFormPersistenceOptions<T> {
  version: number;
  deserialize?: (data: Record<string, unknown>) => T;
}

export function useFormPersistence<T extends FieldValues>(
  storageKey: string,
  reset: UseFormReturn<T>["reset"],
  getValues: UseFormReturn<T>["getValues"],
  options: UseFormPersistenceOptions<T>,
) {
  const { version, deserialize } = options;

  useOnMount(() => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      if (parsed.v !== version) {
        sessionStorage.removeItem(storageKey);
        return;
      }
      const { v: _, ...data } = parsed;
      const restored = deserialize ? deserialize(data) : (data as T);
      reset(restored);
    } catch {
      sessionStorage.removeItem(storageKey);
    }
  });

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({ v: version, ...getValues() }));
  });
}

export function deserializeDateRange(raw: Record<string, unknown>): AdvancedDateRangeValue {
  const shortcut = (raw.shortcut as ShortcutKey | null) ?? null;
  const rawDates = raw.dates as [string | null, string | null] | undefined;

  if (shortcut && shortcut !== "custom") {
    const definition = getShortcutByKey(shortcut);
    if (definition) {
      return { shortcut, dates: definition.getValue() };
    }
  }

  return {
    shortcut,
    dates: [rawDates?.[0] ? dayjs(rawDates[0]) : null, rawDates?.[1] ? dayjs(rawDates[1]) : null],
  };
}
